import React, { useCallback, useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { rupiah, num } from "@/lib/format";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Printer, Trophy } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const RANK_META = [
  { key: "omzet", label: "Omzet", money: true },
  { key: "margin", label: "Margin", money: true },
  { key: "qty", label: "Qty Terjual", money: false },
  { key: "new_customers", label: "Customer Baru", money: false },
  { key: "invoice_count", label: "Jumlah Invoice", money: false },
  { key: "collection", label: "Penagihan Piutang", money: false, suffix: "%" },
];

export default function SalesAnalytics() {
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", month: "", year: "", sales_id: "all", customer_id: "all", product_id: "all", wilayah: "all" });
  const [sel, setSel] = useState("");

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    if (f.month) p.set("month", f.month);
    if (f.year) p.set("year", f.year);
    if (f.sales_id !== "all") p.set("sales_id", f.sales_id);
    if (f.customer_id !== "all") p.set("customer_id", f.customer_id);
    if (f.product_id !== "all") p.set("product_id", f.product_id);
    if (f.wilayah !== "all") p.set("wilayah", f.wilayah);
    return p.toString();
  }, [f]);

  const load = useCallback(async () => {
    try {
      const [a, s, c, p] = await Promise.all([
        api.get(`/analytics/sales?${qs()}`),
        api.get("/sales-agents"), api.get("/customers"), api.get("/products"),
      ]);
      setData(a.data); setAgents(s.data); setCustomers(c.data); setProducts(p.data);
      if (!sel && a.data.sales_kpis.length) setSel(a.data.sales_kpis[0].sales_id);
    } catch (e) { console.error("Gagal memuat analisis:", e); }
  }, [qs, sel]);
  useEffect(() => { load(); }, [load]);

  const kpis = data?.sales_kpis || [];
  const selKpi = kpis.find(k => k.sales_id === sel) || kpis[0];

  const exportExcel = async () => {
    try { await downloadFile(`/analytics/sales/export?${qs()}`, "analisis_sales.xlsx"); }
    catch { toast.error("Gagal export"); }
  };

  return (
    <div data-testid="page-sales-analytics" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap print:hidden">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Analitik</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Analisis Kinerja Sales</h1>
          <p className="text-slate-500 text-sm mt-1">Omzet, margin, penagihan piutang, produk, customer & wilayah per sales.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="btn-export-excel"><Download className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="btn-print-pdf"><Printer className="h-4 w-4 mr-1" />Cetak / PDF</Button>
        </div>
      </div>

      <Card className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 items-end print:hidden" data-testid="analytics-filters">
        <div><Label className="text-xs">Dari</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value, month: "", year: "" })} data-testid="filter-date-from" /></div>
        <div><Label className="text-xs">Sampai</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value, month: "", year: "" })} data-testid="filter-date-to" /></div>
        <div><Label className="text-xs">Bulan</Label><Input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value, date_from: "", date_to: "", year: "" })} data-testid="filter-month" /></div>
        <div><Label className="text-xs">Tahun</Label><Input placeholder="2026" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value, month: "", date_from: "", date_to: "" })} data-testid="filter-year" /></div>
        <div><Label className="text-xs">Sales</Label>
          <Select value={f.sales_id} onValueChange={(v) => setF({ ...f, sales_id: v })}>
            <SelectTrigger data-testid="filter-sales"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{agents.map(a => <SelectItem key={a.sales_id} value={a.sales_id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Customer</Label>
          <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
            <SelectTrigger data-testid="filter-customer"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Produk</Label>
          <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}>
            <SelectTrigger data-testid="filter-product"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Wilayah</Label>
          <Select value={f.wilayah} onValueChange={(v) => setF({ ...f, wilayah: v })}>
            <SelectTrigger data-testid="filter-wilayah"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{(data?.wilayah_options || []).map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <div id="print-area" className="space-y-6">
        <Card data-testid="kpi-table">
          <CardHeader className="py-3"><CardTitle className="text-base">KPI per Sales</CardTitle></CardHeader>
          <CardContent className="pt-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-3">Sales</th><th className="text-right">Omzet</th><th className="text-right">Omzet Terbayar</th><th className="text-right">Qty</th><th className="text-right">Invoice</th><th className="text-right">Cust. Aktif</th><th className="text-right">Cust. Baru</th><th className="text-right">Rata2 Nota</th><th className="text-right">Margin</th><th className="text-right">Margin %</th><th className="text-right">Piutang</th><th className="text-right">Belum Lunas</th><th className="text-right">Penagihan</th><th className="text-right">Target/Bln</th></tr>
              </thead>
              <tbody>
                {kpis.map(k => (
                  <tr key={k.sales_id} className="border-b" data-testid={`kpi-${k.code}`}>
                    <td className="py-2 px-3 font-medium">{k.name} <span className="text-xs text-slate-400 font-mono">{k.code}</span></td>
                    <td className="text-right font-mono">{rupiah(k.omzet)}</td>
                    <td className="text-right font-mono text-blue-700">{rupiah(k.omzet_terbayar)}</td>
                    <td className="text-right font-mono">{num(k.qty)}</td>
                    <td className="text-right font-mono">{k.invoice_count}</td>
                    <td className="text-right font-mono">{k.active_customers}</td>
                    <td className="text-right font-mono">{k.new_customers}</td>
                    <td className="text-right font-mono">{rupiah(k.avg_invoice)}</td>
                    <td className="text-right font-mono text-emerald-700">{rupiah(k.margin)}</td>
                    <td className="text-right font-mono">{k.margin_pct}%</td>
                    <td className="text-right font-mono text-rose-700">{rupiah(k.piutang)}</td>
                    <td className="text-right font-mono">{k.unpaid_count}{k.overdue_count ? <span className="text-rose-600 text-xs"> ({k.overdue_count} telat)</span> : ""}</td>
                    <td className="text-right font-mono">{k.collection_rate}%</td>
                    <td className="text-right font-mono">{k.target ? rupiah(k.target) : "-"}</td>
                  </tr>
                ))}
                {kpis.length === 0 && <tr><td colSpan={14} className="py-10 text-center text-slate-400">Tidak ada data pada filter ini</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-4" data-testid="chart-monthly">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Penjualan per Bulan</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data?.monthly || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} /><Tooltip formatter={(v) => rupiah(v)} /><Line type="monotone" dataKey="omzet" stroke="#0f172a" strokeWidth={2} dot /></LineChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4" data-testid="chart-sales">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Penjualan & Margin per Sales</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={kpis}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} /><Tooltip formatter={(v) => rupiah(v)} /><Legend /><Bar dataKey="omzet" name="Omzet" fill="#0f172a" /><Bar dataKey="margin" name="Margin" fill="#10b981" /></BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4" data-testid="chart-piutang">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Piutang per Sales</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={kpis}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} /><Tooltip formatter={(v) => rupiah(v)} /><Bar dataKey="piutang" name="Piutang" fill="#e11d48" /></BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4" data-testid="chart-target">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Target vs Realisasi (Bulan Ini)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={kpis.filter(k => k.target > 0)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} /><Tooltip formatter={(v) => rupiah(v)} /><Legend /><Bar dataKey="target" name="Target" fill="#cbd5e1" /><Bar dataKey="omzet_month" name="Realisasi" fill="#0f172a" /></BarChart>
            </ResponsiveContainer>
            {kpis.every(k => !k.target) && <div className="text-xs text-slate-400 mt-2">Isi Target Omzet/Bulan di master Sales untuk mengaktifkan grafik ini.</div>}
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card data-testid="chart-top-products">
            <CardHeader className="py-3"><CardTitle className="text-base">Produk Terlaris</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <table className="w-full text-sm"><tbody>
                {(data?.top_products || []).map((p, i) => (
                  <tr key={p.sku} className="border-b"><td className="py-1.5 font-mono text-xs text-slate-400 w-8">{i + 1}</td><td>{p.name} <span className="text-xs text-slate-400 font-mono">{p.sku}</span></td><td className="text-right font-mono">{num(p.qty)}</td><td className="text-right font-mono">{rupiah(p.omzet)}</td></tr>
                ))}
              </tbody></table>
            </CardContent>
          </Card>
          <Card data-testid="chart-top-customers">
            <CardHeader className="py-3"><CardTitle className="text-base">Omzet per Customer</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <table className="w-full text-sm"><tbody>
                {(data?.top_customers || []).map((c, i) => (
                  <tr key={c.customer} className="border-b"><td className="py-1.5 font-mono text-xs text-slate-400 w-8">{i + 1}</td><td>{c.customer}</td><td className="text-right font-mono">{c.count} nota</td><td className="text-right font-mono">{rupiah(c.omzet)}</td></tr>
                ))}
              </tbody></table>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="rankings">
          <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4" />Peringkat Sales (Top 10)</CardTitle></CardHeader>
          <CardContent className="pt-0 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {RANK_META.map(rm => (
              <div key={rm.key}>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">{rm.label}</div>
                <div className="space-y-1">
                  {(data?.rankings?.[rm.key] || []).map((r, i) => (
                    <div key={r.sales_id} className="flex items-center gap-2 text-sm">
                      <span className={`h-5 w-5 rounded text-[10px] font-mono grid place-items-center ${i === 0 ? "bg-amber-400 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
                      <span className="truncate">{r.name}</span>
                      <span className="ml-auto font-mono text-xs">{rm.money ? rupiah(r.value) : num(r.value)}{rm.suffix || ""}</span>
                    </div>
                  ))}
                  {(data?.rankings?.[rm.key] || []).length === 0 && <div className="text-xs text-slate-400">—</div>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {selKpi && (
          <Card data-testid="sales-detail">
            <CardHeader className="py-3 flex-row items-center gap-3 space-y-0">
              <CardTitle className="text-base">Detail per Sales</CardTitle>
              <Select value={selKpi.sales_id} onValueChange={setSel}>
                <SelectTrigger className="w-56" data-testid="select-detail-sales"><SelectValue /></SelectTrigger>
                <SelectContent>{kpis.map(k => <SelectItem key={k.sales_id} value={k.sales_id}>{k.name}</SelectItem>)}</SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="pt-0 grid md:grid-cols-2 gap-4">
              <MiniTable title="Produk Paling Laku" head={["Produk", "Qty", "Omzet", "Kontribusi", "Margin"]} rows={selKpi.top_products.map(p => [`${p.name} (${p.sku})`, num(p.qty), rupiah(p.omzet), `${p.contribution_pct}%`, rupiah(p.margin)])} testid="detail-top-products" />
              <MiniTable title="Produk Paling Sedikit Terjual" head={["Produk", "Qty", "Omzet"]} rows={selKpi.least_products.map(p => [`${p.name} (${p.sku})`, num(p.qty), rupiah(p.omzet)])} testid="detail-least-products" />
              <MiniTable title="Customer Terbesar" head={["Customer", "Omzet", "Nota"]} rows={selKpi.top_customers.map(c => [c.customer, rupiah(c.omzet), c.count])} testid="detail-top-customers" />
              <MiniTable title="Customer Paling Sering Membeli" head={["Customer", "Nota", "Omzet"]} rows={selKpi.frequent_customers.map(c => [c.customer, c.count, rupiah(c.omzet)])} testid="detail-frequent-customers" />
              <MiniTable title="Lama Tidak Membeli (>30 hari)" head={["Customer", "Terakhir", "Hari"]} rows={selKpi.dormant_customers.map(c => [c.customer, c.last_date, `${c.days} hari`])} testid="detail-dormant-customers" />
              <MiniTable title="Piutang Terbesar" head={["Customer", "Piutang"]} rows={selKpi.receivable_customers.map(c => [c.customer, rupiah(c.piutang)])} testid="detail-receivable-customers" />
            </CardContent>
          </Card>
        )}

        <Card data-testid="wilayah-table">
          <CardHeader className="py-3"><CardTitle className="text-base">Analisis Wilayah</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-3">Wilayah</th><th className="text-right">Omzet</th><th className="text-right">Margin</th><th className="text-right">Customer</th><th>Produk Terlaris</th></tr>
              </thead>
              <tbody>
                {(data?.wilayah || []).map(w => (
                  <tr key={w.wilayah} className="border-b"><td className="py-2 px-3 font-medium">{w.wilayah}</td><td className="text-right font-mono">{rupiah(w.omzet)}</td><td className="text-right font-mono text-emerald-700">{rupiah(w.margin)}</td><td className="text-right font-mono">{w.customer_count}</td><td>{w.top_product}</td></tr>
                ))}
                {(data?.wilayah || []).length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Belum ada data wilayah — isi field Wilayah di master Pelanggan</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MiniTable({ title, head, rows, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">{title}</div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-400 border-b">{head.map((h, i) => <th key={h} className={`py-1 ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i} className="border-b last:border-0">{r.map((c, j) => <td key={j} className={`py-1.5 ${j > 0 ? "text-right font-mono text-xs" : ""}`}>{c}</td>)}</tr>)}
          {rows.length === 0 && <tr><td colSpan={head.length} className="py-4 text-center text-xs text-slate-400">—</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
